import * as React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

const Profile = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [userData, setUserData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const token = localStorage.getItem("token");

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://api.clever-school.com/api/me",
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setUserData(response.data);
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          window.location.href = "/login";
          return;
        }
        setError(t("please check your internet and try again")); // Fixed typo in translation key
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    } else {
      window.location.href = "/login";
    }
  }, [token, t]);

  const direction = ["ar", "ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ar", "ku"].includes(i18n.language) ? "right" : "left";

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography
        variant="h6"
        color="error"
        sx={{ textAlign: "center", mt: 4 }}
      >
        {error}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        direction,
        textAlign,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: 3,
        backgroundColor: "#f5f5f5",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 600,
          borderRadius: 2,
          boxShadow: 3,
          "&:hover": { boxShadow: 6 },
        }}
      >
        <CardContent>
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              mb: 3,
              color: "primary.main",
              fontWeight: "bold",
            }}
          >
            {t("userProfile")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              "& > div": {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: 2,
                backgroundColor: "#f8f9fa",
                borderRadius: 1,
              },
            }}
          >
            <Box>
              <Typography variant="subtitle1">{t("username")}</Typography>
              <Chip
                label={userData.username}
                color="primary"
                variant="outlined"
              />
            </Box>
            {userData.role !== "superadmin" && (
              <Box>
                <Typography variant="subtitle1">{t("branch")}</Typography>
                <Chip
                  label={t(`branch1.${userData.branch_id}`)}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
            <Box>
              <Typography variant="subtitle1">{t("role1")}</Typography>
              <Chip
                label={t(userData.role)}
                color="secondary"
                sx={{ textTransform: "capitalize" }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/")}
              sx={{
                width: 120,
                "&:hover": { transform: "scale(1.05)" },
              }}
            >
              {t("back")}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Profile;
